import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Tabs,
  Box,
  Loader,
  Center,
  Button,
  Table,
  Stack,
  ActionIcon,
  Menu,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Users,
  AlertTriangle,
  ArrowLeft,
  Hash,
  Briefcase,
  Settings,
} from 'react-feather';
import { partnersApi } from '../../services/api/partners';
import type {
  Partner,
  PartnerContactWithCohortCount,
  PartnerContactCohort,
  PartnerCoachListItem,
  CoachStatus,
} from '../../types';
import { PartnerContactModal } from './components/PartnerContactModal';
import { EditPartnerModal } from '../../components/partners/EditPartnerModal';
import { SortableTableHeader, ColumnSettingsPopover, EmptyState } from '../../components/common';
import { useColumnConfig, type ColumnConfig } from '../../hooks';
import classes from './PartnerDetailPage.module.css';

// Default column configuration for contacts table
const DEFAULT_CONTACT_COLUMNS: ColumnConfig[] = [
  { id: 'name', label: 'Name', visible: true, sortable: true },
  { id: 'email', label: 'Email', visible: true, sortable: true },
  { id: 'title', label: 'Job Title', visible: true, sortable: true },
  { id: 'cohorts', label: '# Primary Contact', visible: true, sortable: true },
];

// Default column configuration for partner coaches table
const DEFAULT_COACH_COLUMNS: ColumnConfig[] = [
  { id: 'name', label: 'Name', visible: true, sortable: true },
  { id: 'email', label: 'Email', visible: true, sortable: true },
  { id: 'timezone', label: 'Timezone', visible: true, sortable: true },
  { id: 'groups', label: 'Assigned Groups', visible: true, sortable: true },
  { id: 'status', label: 'Status', visible: true, sortable: true },
];

const COACH_STATUS_DISPLAY: Record<CoachStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'medleyGreen' },
  inactive: { label: 'Inactive', color: 'gray' },
};

export function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [contacts, setContacts] = useState<PartnerContactWithCohortCount[]>([]);
  const [coaches, setCoaches] = useState<PartnerCoachListItem[]>([]);
  const [coachCount, setCoachCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [coachesLoading, setCoachesLoading] = useState(true);

  // Persist active tab in URL
  const activeTab = searchParams.get('tab') || 'users';
  const setActiveTab = (tab: string | null) => {
    if (tab) {
      setSearchParams({ tab }, { replace: true });
    }
  };

  // Modal state
  const [contactModalOpened, { open: openContactModal, close: closeContactModal }] = useDisclosure(false);
  const [editingContact, setEditingContact] = useState<PartnerContactWithCohortCount | null>(null);
  const [editPartnerOpened, { open: openEditPartner, close: closeEditPartner }] = useDisclosure(false);

  // Use the reusable column config hook (per-partner preference key)
  const {
    columns: contactColumns,
    setColumns: setContactColumns,
    sortConfig,
    handleSort,
  } = useColumnConfig({
    preferenceKey: `partner_contacts_${id}`,
    defaultColumns: DEFAULT_CONTACT_COLUMNS,
    defaultPageSize: 25,
  });

  // Coach column config
  const {
    columns: coachColumns,
    setColumns: setCoachColumns,
    sortConfig: coachSortConfig,
    handleSort: handleCoachSort,
  } = useColumnConfig({
    preferenceKey: `partner_coaches_${id}`,
    defaultColumns: DEFAULT_COACH_COLUMNS,
    defaultPageSize: 50,
  });

  // Sort coaches client-side based on coachSortConfig
  const sortedCoaches = useMemo(() => {
    if (coaches.length === 0) return [];
    if (!coachSortConfig) return [...coaches];

    const sorted = [...coaches].sort((a, b) => {
      let comparison = 0;
      switch (coachSortConfig.key) {
        case 'name':
          comparison = `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(
            `${b.first_name || ''} ${b.last_name || ''}`,
          );
          break;
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'timezone':
          comparison = (a.home_base_timezone || '').localeCompare(b.home_base_timezone || '');
          break;
        case 'groups':
          comparison = a.assigned_group_count - b.assigned_group_count;
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          return 0;
      }
      return coachSortConfig.order === 'DESC' ? -comparison : comparison;
    });

    return sorted;
  }, [coaches, coachSortConfig]);

  // Sort contacts client-side based on sortConfig
  const sortedContacts = useMemo(() => {
    if (contacts.length === 0) return [];
    if (!sortConfig) return [...contacts];

    const sorted = [...contacts].sort((a, b) => {
      let comparison = 0;
      switch (sortConfig.key) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'title':
          comparison = (a.job_title || '').localeCompare(b.job_title || '');
          break;
        case 'cohorts':
          comparison = a.primary_cohort_count - b.primary_cohort_count;
          break;
        default:
          return 0;
      }
      return sortConfig.order === 'DESC' ? -comparison : comparison;
    });

    return sorted;
  }, [contacts, sortConfig]);

  // Render column header based on column config
  const renderContactColumnHeader = (column: ColumnConfig) => {
    if (column.sortable) {
      return (
        <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
          <SortableTableHeader
            label={column.label.toUpperCase()}
            sortKey={column.id}
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </Table.Th>
      );
    }
    return (
      <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
        {column.label.toUpperCase()}
      </Table.Th>
    );
  };

  // Render cell content based on column id
  const renderContactCellContent = (column: ColumnConfig, contact: PartnerContactWithCohortCount) => {
    switch (column.id) {
      case 'name':
        return (
          <Text fw={500}>
            {contact.first_name} {contact.last_name}
          </Text>
        );
      case 'email':
        return <Text size="sm">{contact.email}</Text>;
      case 'title':
        return (
          <Text size="sm" c={contact.job_title ? undefined : 'dimmed'}>
            {contact.job_title || '—'}
          </Text>
        );
      case 'cohorts':
        return <Text size="sm">{contact.primary_cohort_count}</Text>;
      default:
        return <Text size="sm">—</Text>;
    }
  };

  // Render coach column header
  const renderCoachColumnHeader = (column: ColumnConfig) => {
    if (column.sortable) {
      return (
        <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
          <SortableTableHeader
            label={column.label.toUpperCase()}
            sortKey={column.id}
            currentSort={coachSortConfig}
            onSort={handleCoachSort}
          />
        </Table.Th>
      );
    }
    return (
      <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
        {column.label.toUpperCase()}
      </Table.Th>
    );
  };

  // Render coach cell content
  const renderCoachCellContent = (column: ColumnConfig, coach: PartnerCoachListItem) => {
    switch (column.id) {
      case 'name':
        return (
          <Text
            fw={500}
            c="medleyGreen"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/coaches/${coach.id}`)}
          >
            {coach.first_name} {coach.last_name}
          </Text>
        );
      case 'email':
        return <Text size="sm">{coach.email}</Text>;
      case 'timezone':
        return (
          <Text size="sm" c={coach.home_base_timezone ? undefined : 'dimmed'}>
            {coach.home_base_timezone || '—'}
          </Text>
        );
      case 'groups':
        return <Text size="sm">{coach.assigned_group_count}</Text>;
      case 'status': {
        const statusInfo = COACH_STATUS_DISPLAY[coach.status] || { label: coach.status, color: 'gray' };
        return (
          <Badge color={statusInfo.color} variant="light" size="sm" tt="uppercase">
            {statusInfo.label}
          </Badge>
        );
      }
      default:
        return <Text size="sm">—</Text>;
    }
  };

  // Fetch partner and coach count on mount
  useEffect(() => {
    const fetchPartner = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [data, coachResult] = await Promise.all([
          partnersApi.getById(id),
          partnersApi.listCoaches(id, { limit: 1 }),
        ]);
        setPartner(data);
        setCoachCount(coachResult.pagination.total);
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to fetch partner',
          color: 'red',
        });
        navigate('/partners');
      } finally {
        setLoading(false);
      }
    };

    fetchPartner();
  }, [id, navigate]);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!id) return;

    try {
      setContactsLoading(true);
      const data = await partnersApi.listContacts(id);
      setContacts(data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to fetch contacts',
        color: 'red',
      });
    } finally {
      setContactsLoading(false);
    }
  }, [id]);

  // Fetch coaches
  const fetchCoaches = useCallback(async () => {
    if (!id) return;

    try {
      setCoachesLoading(true);
      const result = await partnersApi.listCoaches(id);
      setCoaches(result.data);
      setCoachCount(result.pagination.total);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to fetch partner coaches',
        color: 'red',
      });
    } finally {
      setCoachesLoading(false);
    }
  }, [id]);

  // Fetch contacts only when the users tab is active
  useEffect(() => {
    if (activeTab === 'users') {
      fetchContacts();
    }
  }, [activeTab, fetchContacts]);

  // Fetch coaches only when the coaches tab is active
  useEffect(() => {
    if (activeTab === 'coaches') {
      fetchCoaches();
    }
  }, [activeTab, fetchCoaches]);

  // Handle add contact
  const handleAddContact = () => {
    setEditingContact(null);
    openContactModal();
  };

  // Handle edit contact
  const handleEditContact = (contact: PartnerContactWithCohortCount) => {
    setEditingContact(contact);
    openContactModal();
  };

  // Handle delete contact
  const handleDeleteContact = async (contact: PartnerContactWithCohortCount) => {
    if (!id) return;

    // If contact is primary on cohorts, show warning
    if (contact.primary_cohort_count > 0) {
      try {
        const cohorts = await partnersApi.getContactCohorts(id, contact.id);
        confirmDeleteWithWarning(contact, cohorts);
      } catch {
        // If we can't fetch cohorts, still allow delete with generic warning
        confirmDeleteWithWarning(contact, []);
      }
    } else {
      confirmDelete(contact);
    }
  };

  const confirmDelete = (contact: PartnerContactWithCohortCount) => {
    modals.openConfirmModal({
      title: 'Delete Contact',
      children: (
        <Text size="sm">
          Are you sure you want to delete{' '}
          <strong>
            {contact.first_name} {contact.last_name}
          </strong>
          ? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => performDelete(contact),
    });
  };

  const confirmDeleteWithWarning = (
    contact: PartnerContactWithCohortCount,
    cohorts: PartnerContactCohort[],
  ) => {
    modals.openConfirmModal({
      title: 'Delete Contact',
      children: (
        <Stack gap="md">
          <Group gap="sm" c="orange">
            <AlertTriangle size={20} />
            <Text size="sm" fw={500}>
              Warning: This contact is the Primary Contact for {contact.primary_cohort_count} cohort
              {contact.primary_cohort_count !== 1 ? 's' : ''}
            </Text>
          </Group>
          {cohorts.length > 0 && (
            <Box>
              <Text size="sm" c="dimmed" mb="xs">
                Affected cohorts:
              </Text>
              <Stack gap={4}>
                {cohorts.map(cohort => (
                  <Text key={cohort.id} size="sm">
                    {cohort.name}
                  </Text>
                ))}
              </Stack>
            </Box>
          )}
          <Text size="sm">
            Are you sure you want to delete{' '}
            <strong>
              {contact.first_name} {contact.last_name}
            </strong>
            ? The affected cohorts will have no primary contact assigned.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Delete Anyway', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => performDelete(contact),
    });
  };

  const performDelete = async (contact: PartnerContactWithCohortCount) => {
    if (!id) return;

    try {
      await partnersApi.deleteContact(id, contact.id);
      notifications.show({
        title: 'Success',
        message: 'Contact deleted successfully',
        color: 'green',
      });
      fetchContacts();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete contact',
        color: 'red',
      });
    }
  };

  // Handle contact saved (created or updated)
  const handleContactSaved = () => {
    closeContactModal();
    setEditingContact(null);
    fetchContacts();
  };

  // Partner-level actions
  const handlePartnerSaved = (updated: Partner) => {
    setPartner(prev => (prev ? { ...prev, ...updated } : updated));
    closeEditPartner();
  };

  const performPartnerDelete = async () => {
    if (!partner) return;
    try {
      await partnersApi.delete(partner.id);
      notifications.show({
        title: 'Success',
        message: 'Partner deleted successfully',
        color: 'green',
      });
      navigate('/partners');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete partner',
        color: 'red',
      });
    }
  };

  const handleDeletePartner = async () => {
    if (!partner) return;

    let impact = { active_cohort_count: 0, partner_coach_count: 0 };
    try {
      impact = await partnersApi.getDeletionImpact(partner.id);
    } catch {
      // Fall through with zero counts — backend enforces existence and we still
      // allow the admin to confirm; the modal will just omit the warning block.
    }

    const hasDependents = impact.active_cohort_count > 0 || impact.partner_coach_count > 0;
    const dependentFragments: string[] = [];
    if (impact.active_cohort_count > 0) {
      dependentFragments.push(
        `${impact.active_cohort_count} active cohort${impact.active_cohort_count === 1 ? '' : 's'}`,
      );
    }
    if (impact.partner_coach_count > 0) {
      dependentFragments.push(
        `${impact.partner_coach_count} assigned coach${impact.partner_coach_count === 1 ? '' : 'es'}`,
      );
    }

    modals.openConfirmModal({
      title: 'Delete Partner',
      children: (
        <Stack gap="md">
          {hasDependents && (
            <Group gap="sm" c="orange" align="flex-start">
              <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
              <Text size="sm" fw={500}>
                Warning: This partner has {dependentFragments.join(' and ')}. Deleting will orphan
                these records.
              </Text>
            </Group>
          )}
          <Text size="sm">
            Are you sure you want to delete <strong>&apos;{partner.name}&apos;</strong>? This action
            cannot be undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: hasDependents ? 'Delete Anyway' : 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => performPartnerDelete(),
    });
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!partner) {
    return (
      <Container size="xl" py="xl">
        <Text>Partner not found</Text>
      </Container>
    );
  }

  return (
    <>
      {/* Full-width Header */}
      <Box className={classes.fullWidthHeader}>
        <Container size="xl">
          {/* Header Content */}
          <Box className={classes.headerContent}>
            {/* Top Row: Back + Title + Badge + Settings */}
            <Group justify="space-between" mb="sm">
              <Group gap="md">
                {/* Back Button */}
                <ActionIcon
                  variant="filled"
                  color="medleyGreen"
                  size="lg"
                  radius="xl"
                  onClick={() => navigate('/partners')}
                >
                  <ArrowLeft size={18} />
                </ActionIcon>

                {/* Title + Badge */}
                <Group gap="sm">
                  <Text className={classes.partnerTitle}>{partner.name}</Text>
                  <Badge
                    color={partner.is_active ? 'medleyGreen' : 'gray'}
                    variant={partner.is_active ? 'filled' : 'light'}
                    size="lg"
                    tt="uppercase"
                  >
                    {partner.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Group>
              </Group>

              {/* Settings Button */}
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <Button variant="outline" leftSection={<Settings size={16} />}>
                    Settings
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<Edit size={14} />} onClick={openEditPartner}>
                    Edit Partner
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<Trash2 size={14} />}
                    onClick={handleDeletePartner}
                  >
                    Delete Partner
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            {/* Subtitle Row */}
            <Group className={classes.subtitle} ml={52}>
              {partner.code && (
                <Group gap="xs">
                  <Hash size={14} />
                  <Text size="sm">{partner.code}</Text>
                </Group>
              )}
              <Group gap="xs">
                <Briefcase size={14} />
                <Text size="sm">{partner.program_count} program{partner.program_count !== 1 ? 's' : ''}</Text>
              </Group>
            </Group>
          </Box>

          {/* Tabs Row - inside header */}
          <Box className={classes.tabsRow}>
            <Tabs value={activeTab} onChange={setActiveTab} className="tabs-underline">
              <Tabs.List>
                <Tabs.Tab value="users">Partner Users</Tabs.Tab>
                <Tabs.Tab value="coaches">
                  Partner Coaches{coachCount > 0 ? ` (${coachCount})` : ''}
                </Tabs.Tab>
                <Tabs.Tab value="programs">Programs</Tabs.Tab>
                <Tabs.Tab value="cohorts">Cohorts</Tabs.Tab>
              </Tabs.List>
            </Tabs>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container size="xl" py="xl">
        {/* Tab Panels */}
        <Tabs value={activeTab} onChange={setActiveTab}>

        <Tabs.Panel value="users" pt="lg">
          <Card withBorder className={classes.sectionCard}>
            <Group justify="space-between" mb="md">
              <Title order={4}>Partner Users</Title>
              <Group>
                <ColumnSettingsPopover
                  columns={contactColumns}
                  onColumnsChange={setContactColumns}
                />
                <Button leftSection={<Plus size={16} />} size="sm" onClick={handleAddContact}>
                  Add Contact
                </Button>
              </Group>
            </Group>

            {contactsLoading ? (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            ) : contacts.length === 0 ? (
              <EmptyState
                icon={<Users size={32} strokeWidth={1.5} />}
                title="No contacts yet"
                description="Add contacts to manage who receives notifications."
                action={
                  <Button
                    leftSection={<Plus size={16} />}
                    size="sm"
                    variant="light"
                    onClick={handleAddContact}
                  >
                    Add Contact
                  </Button>
                }
              />
            ) : (
              <Table.ScrollContainer minWidth={600}>
                <Table verticalSpacing="sm" horizontalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      {contactColumns
                        .filter(col => col.visible)
                        .map(col => renderContactColumnHeader(col))}
                      <Table.Th className={classes.actionsColumn}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sortedContacts.map(contact => (
                      <Table.Tr key={contact.id} className={classes.tableRow}>
                        {contactColumns
                          .filter(col => col.visible)
                          .map(col => (
                            <Table.Td
                              key={col.id}
                              ta={col.id === 'cohorts' ? 'center' : undefined}
                            >
                              {renderContactCellContent(col, contact)}
                            </Table.Td>
                          ))}
                        <Table.Td>
                          <Menu shadow="md" width={160} position="bottom-end">
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                onClick={e => e.stopPropagation()}
                              >
                                <MoreVertical size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<Edit size={14} />}
                                onClick={() => handleEditContact(contact)}
                              >
                                Edit
                              </Menu.Item>
                              <Menu.Item
                                color="red"
                                leftSection={<Trash2 size={14} />}
                                onClick={() => handleDeleteContact(contact)}
                              >
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="coaches" pt="lg">
          <Card withBorder className={classes.sectionCard}>
            <Group justify="space-between" mb="md">
              <Title order={4}>Partner Coaches</Title>
              <ColumnSettingsPopover
                columns={coachColumns}
                onColumnsChange={setCoachColumns}
              />
            </Group>

            {coachesLoading ? (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            ) : coaches.length === 0 ? (
              <EmptyState
                icon={<Users size={32} strokeWidth={1.5} />}
                title="No Partner Coaches"
                description="No Partner Coaches have been added for this partner."
              />
            ) : (
              <Table.ScrollContainer minWidth={700}>
                <Table verticalSpacing="sm" horizontalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      {coachColumns
                        .filter(col => col.visible)
                        .map(col => renderCoachColumnHeader(col))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sortedCoaches.map(coach => (
                      <Table.Tr
                        key={coach.id}
                        className={classes.tableRow}
                        onClick={() => navigate(`/coaches/${coach.id}`)}
                      >
                        {coachColumns
                          .filter(col => col.visible)
                          .map(col => (
                            <Table.Td
                              key={col.id}
                              ta={col.id === 'groups' ? 'center' : undefined}
                            >
                              {renderCoachCellContent(col, coach)}
                            </Table.Td>
                          ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="programs" pt="lg">
          <Card withBorder>
            <Text ta="center" c="dimmed" py="xl">
              Programs feature coming soon
            </Text>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="cohorts" pt="lg">
          <Card withBorder>
            <Text ta="center" c="dimmed" py="xl">
              Cohorts feature coming soon
            </Text>
          </Card>
        </Tabs.Panel>
      </Tabs>

      {/* Contact Modal */}
      <PartnerContactModal
        opened={contactModalOpened}
        onClose={() => {
          closeContactModal();
          setEditingContact(null);
        }}
        partnerId={id!}
        contact={editingContact}
        onSaved={handleContactSaved}
      />

      {/* Edit Partner Modal */}
      <EditPartnerModal
        opened={editPartnerOpened}
        onClose={closeEditPartner}
        partner={partner}
        onSaved={handlePartnerSaved}
      />
      </Container>
    </>
  );
}

export default PartnerDetailPage;
